import {
    Dialog,
    DialogPanel,
    Popover,
    PopoverButton,
    PopoverGroup,
    PopoverPanel,
} from "@headlessui/react";
import { CogIcon } from "@heroicons/react/20/solid";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";

import DesktopNavItem from "./desktopNavItem";
import { Link, usePage, router } from "@inertiajs/react";
import Avatar from "../common/avatars/avatar";
import { Trello, Layers, LogOut } from "react-feather";
import { FormattedMessage, useIntl } from "react-intl";
import { useEffect, useState } from "react";
import MobileNavItem from "./mobileNavItem";

export interface NavPageInterface {
    children: NavPageInterface[];
    depth: number;
    page: {
        entry_id?: string;
        id: string;
        permalink?: string;
        uri?: string;
        url?: string;
        hide_on_desktop?: boolean;
        icon?: string;
        inertia?: boolean;
        raise_when_logged_in: boolean;
        section?: { value: string; label: string; key: string };
        title: string;
        description?: string;
        feature_flags: string[];
        grouped?: boolean;
        external?: boolean;
    };
}

export default function Header() {
    const intl = useIntl();
    const { auth, permissions, nav, logos } = usePage().props;
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    // @ts-ignore
    useEffect(() => {
        setTimeout(() => {
            // @ts-ignore
            Recite.load({
                enableButton: ".accessibility",
            });
        }, 100);
    }, []);

    const shownItems = nav.map((item) => {
        return {
            ...item,
            children: item.children.filter(
                (item) =>
                    !item.page.feature_flags?.length ||
                    item.page.feature_flags.some((i) =>
                        auth?.user?.permissions?.includes(i),
                    ),
            ),
        };
    });
    const getDesktopItems = (items) => {
        let result = [] as any[];
        const resources = {
            page: {
                title: intl.formatMessage({ id: "About" }),
                grouped: true,
            },
            children: [] as any[],
        };
        items
            .filter((item) => !item?.page?.hide_on_desktop)
            .forEach((element) => {
                if (auth?.user) {
                    if (element.page?.raise_when_logged_in) {
                        result = [...result, ...element.children];
                    } else {
                        resources.children.push(element);
                    }
                } else {
                    result.push(element);
                }
            });
        if (resources.children.length) {
            result.push(resources);
        }
        return result;
    };
    const desktopItems = getDesktopItems(shownItems);
    return (
        <div className="sm:border-b-2 sm:border-gray-100 relative rotate-0 z-50">
            <div className="flex items-center justify-between py-6 md:justify-start px-6 max-w-[1600px] mx-auto">
                <div className="flex justify-start mr-2 lg:mr-8 xl:mr-14">
                    <Link href={route("home")}>
                        <span className="sr-only">Voice</span>
                        <img
                            className="w-26 min-w-26"
                            src={
                                logos?.colour_logo?.permalink ||
                                "/images/voice-logo.svg"
                            }
                            alt="Voice logo"
                        />
                    </Link>
                </div>
                <div className="-my-2 -mr-2 md:hidden">
                    <button
                        type="button"
                        className="inline-flex items-center justify-center rounded-md bg-white p-2 text-gray-900 hover:bg-gray-100 hover:text-gray-500 focus:outline-hidden focus:ring-2 focus:ring-inset focus:ring-red-500"
                        aria-expanded="false"
                        onClick={() => setMobileMenuOpen((prev) => !prev)}
                    >
                        <span className="sr-only">
                            <FormattedMessage id="Open menu" />
                        </span>
                        <Bars3Icon className="size-6" />
                    </button>
                </div>
                <nav className="hidden pt-2 md:flex">
                    <PopoverGroup className="hidden md:flex gap-x-4 lg:gap-x-8">
                        {desktopItems.map((item) => (
                            <DesktopNavItem
                                key={item.page.id ?? item.page.title}
                                item={item}
                            />
                        ))}
                    </PopoverGroup>
                </nav>

                <div className="hidden space-x-8 pt-2 items-center justify-end md:flex md:flex-1 lg:w-0">
                    <button
                        type="button"
                        className="accessibility whitespace-nowrap text-base font-medium text-gray-900 border-b-2 border-b-white hover:border-b-2 hover:border-b-gray-900 hover:text-gray-900 transition"
                    >
                        <FormattedMessage id="Accessibility" />
                    </button>
                    {auth.user ? (
                        <Popover className="relative">
                            <PopoverButton className="flex max-w-xs items-center rounded-full bg-white/80 text-sm focus:outline-hidden focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 lg:hover:bg-gray-50 backdrop-blur-md">
                                <Avatar user={auth.user} size="lg" />
                                <span className="sr-only">
                                    <span>
                                        <FormattedMessage id="Open user menu for" />
                                    </span>
                                    {auth.user.first_name} {auth.user.last_name}
                                </span>
                            </PopoverButton>

                            <PopoverPanel
                                className="absolute right-0 z-50 w-48 origin-top-right rounded-[1.25rem] bg-white/80 p-4 shadow-lg ring-1 ring-black/5 focus:outline-hidden backdrop-blur-md"
                                anchor={{ to: "bottom end", gap: "36px" }}
                            >
                                <div className="flex flex-col items-center py-6">
                                    <Avatar user={auth.user} size="lg" />
                                    <span className="text-sm pt-2">
                                        {auth.user.first_name}{" "}
                                        {auth.user.last_name}
                                    </span>
                                    <span className="text-xs text-gray-600 capitalize">
                                        {auth?.user?.role || "Member"}
                                    </span>
                                </div>
                                {permissions.view_dashboard && (
                                    <a
                                        href={route("dashboard")}
                                        className="flex items-center px-4 py-3 text-sm text-gray-900 hover:bg-gray-100 hover:text-gray-800 rounded-lg"
                                    >
                                        <Trello className="h-5 w-5 text-gray-700 mr-3" />
                                        <FormattedMessage id="Dashboard" />
                                    </a>
                                )}
                                <a
                                    href={route("my-voice")}
                                    className="flex items-center px-4 py-3 text-sm text-gray-900 hover:bg-gray-100 hover:text-gray-800 rounded-lg"
                                >
                                    <Layers className="h-5 w-5 text-gray-700 mr-3" />
                                    <FormattedMessage id="My Voice" />
                                </a>
                                <Link
                                    href={route("user-settings")}
                                    className="flex items-center px-4 py-3 text-sm text-gray-900 hover:bg-gray-100 hover:text-gray-800 rounded-lg"
                                >
                                    <CogIcon className="h-5 w-5 text-gray-700 mr-3" />
                                    <FormattedMessage id="Settings" />
                                </Link>
                                <hr className="w-full h-[2px] bg-gray-100 my-2" />
                                <button
                                    onClick={() => router.post(route("logout"))}
                                    className="flex items-center px-4 py-3 text-sm text-gray-900 hover:bg-gray-100 hover:text-gray-800 rounded-lg w-full"
                                >
                                    <LogOut className="h-5 w-5 text-gray-700 mr-3" />
                                    <FormattedMessage id="Logout" />
                                </button>
                            </PopoverPanel>
                        </Popover>
                    ) : (
                        <>
                            <a
                                href={route("login")}
                                className="whitespace-nowrap text-base font-medium text-gray-900 border-b-2 border-b-white hover:border-b-2 hover:border-b-gray-900 hover:text-gray-900 transition"
                            >
                                <FormattedMessage id="Log in" />
                            </a>
                            <a
                                href={route("register")}
                                className="-mt-1 ml-8 inline-flex items-center justify-center whitespace-nowrap rounded-lg border border-transparent bg-gray-900 px-4 py-2 text-base font-medium text-white shadow-xs hover:bg-gray-700"
                            >
                                <FormattedMessage id="Get started" />
                            </a>
                        </>
                    )}
                </div>

                <Dialog
                    as="div"
                    className="md:hidden"
                    open={mobileMenuOpen}
                    onClose={setMobileMenuOpen}
                >
                    <DialogPanel className="absolute inset-x-0 top-0 origin-top-right transform transition md:hidden z-999 h-full">
                        <div className="bg-white h-full flex flex-col justify-between">
                            <div className="flex items-center justify-between border-b px-8 py-5">
                                <div>
                                    <a href={route("home")}>
                                        <span className="sr-only">
                                            <FormattedMessage id="Voice" />
                                        </span>
                                        <img
                                            className="h-6 w-auto"
                                            src="/images/voice-logo.svg"
                                            alt="Voice logo"
                                        />
                                    </a>
                                </div>
                                <div className="-mr-2">
                                    <button
                                        className="inline-flex items-center justify-center rounded-md bg-white p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-hidden focus:ring-2 focus:ring-inset focus:ring-blue-500"
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        <span className="sr-only">
                                            <FormattedMessage id="Close menu" />
                                        </span>
                                        <XMarkIcon className="h-6 w-6" />
                                    </button>
                                </div>
                            </div>
                            <div className="pb-6 flex-grow overflow-y-auto">
                                <div className="">
                                    <nav className="grid divide-y divide-gray-200">
                                        {desktopItems.map((item) => (
                                            <MobileNavItem
                                                item={item}
                                                key={
                                                    item.page.id ??
                                                    item.page.title
                                                }
                                            />
                                        ))}
                                    </nav>
                                </div>
                            </div>
                            <div className="p-6">
                                {auth.user ? (
                                    <button
                                        onClick={() =>
                                            router.post(route("logout"))
                                        }
                                        className="items-center px-4 py-2.5 border border-transparent shadow-xs text-base font-medium rounded-md text-white bg-gray-900 hover:bg-gray-800 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 active:bg-gray-900 disabled:opacity-25 transition ease-in-out duration-150 flex w-full justify-center"
                                    >
                                        <FormattedMessage id="Logout" />
                                    </button>
                                ) : (
                                    <>
                                        <a
                                            href={route("register")}
                                            className="flex w-full items-center justify-center rounded-md border border-transparent bg-gray-900 px-4 py-2 text-base font-medium text-white shadow-xs hover:bg-gray-700"
                                        >
                                            <FormattedMessage id="Get started" />
                                        </a>
                                        <p className="mt-6 text-center text-base font-medium text-gray-500">
                                            <FormattedMessage id="Existing Voice Member?" />
                                            <a
                                                href={route("login")}
                                                className="text-red-600 hover:text-red-500"
                                            >
                                                {" "}
                                                <FormattedMessage id="Sign in" />
                                            </a>
                                        </p>
                                    </>
                                )}
                            </div>
                        </div>
                    </DialogPanel>
                </Dialog>
            </div>
        </div>
    );
}
